import { useTranslation } from "@vintecc/components";
import type { TranslationKey, TranslationValues } from "@vintecc/components";

type TypedTranslationMap<TKey extends string> = Partial<Record<TKey, string>>;

type UseTypedTranslationOptions = {
  isReady?: boolean;
};

type TypedTranslateFn<TKey extends string, TTranslations extends TypedTranslationMap<TKey>, TPluralKey extends TKey> = {
  (key: TPluralKey, values: { count: number } & Record<string, unknown>): string;
  <TKeyValue extends Exclude<TKey, TPluralKey>>(
    key: TKeyValue,
    defaultValueOrValues?: string | TranslationValues,
    values?: TranslationValues,
  ): TKeyValue extends keyof TTranslations ? TTranslations[TKeyValue] : string;
  (key: TranslationKey, defaultValueOrValues?: string | TranslationValues, values?: TranslationValues): string;
};

/**
 * Generic typed translation hook.
 *
 * Provide your module's key union as TKey and, optionally, the plural key
 * union as TPluralKey. The t() function then enforces { count: number } for
 * plural keys at compile time.
 *
 * In practice, use the generated useXxxTranslation() hooks instead of calling
 * this directly — they wire up TKey, TTranslations, and TPluralKey automatically.
 *
 * @example
 * // Calling a plural key:
 * t("home.warnings.apiTokens.message", { count: 3 })  // ✅
 * t("home.warnings.apiTokens.message")                // ❌ compile error — count missing
 *
 * // Calling a regular key:
 * t("home.warnings.apiTokens.title")                  // ✅
 */
export function useTypedTranslation<TKey extends string, TTranslations extends TypedTranslationMap<TKey> = object, TPluralKey extends TKey = never>(
  typedTranslations?: TTranslations,
  pluralKeys?: Partial<Record<TPluralKey, true>>,
  options?: UseTypedTranslationOptions,
) {
  const culture = useTranslation();

  // Used for type inference only; runtime values still come from CultureProvider/i18next.
  void typedTranslations;
  void pluralKeys;

  const tBase = (key: TKey | TranslationKey, defaultValueOrValues?: string | TranslationValues, values?: TranslationValues): string => {
    if (options?.isReady === false) {
      return typeof defaultValueOrValues === "string" ? defaultValueOrValues : "";
    }

    const interpolationValues = typeof defaultValueOrValues === "string" ? values : (defaultValueOrValues ?? values);

    const defaultValue = typeof defaultValueOrValues === "string" ? defaultValueOrValues : undefined;
    return culture.t(key as TranslationKey | (string & {}), defaultValue, interpolationValues);
  };

  const t = tBase as TypedTranslateFn<TKey, TTranslations, TPluralKey>;

  return { ...culture, t };
}
